import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Scissors, 
  Users, 
  Store, 
  Search, 
  MessageSquare, 
  CreditCard, 
  Shield, 
  Zap, 
  BarChart3, 
  Globe, 
  Camera, 
  Truck, 
  Star,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const ServicesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('customers');

  const customerServices = [
    {
      icon: Search,
      title: "Find Perfect Tailors",
      description: "Browse verified tailors with detailed profiles, ratings, and portfolios. Find the perfect match for your style and requirements.",
      features: ["Advanced search filters", "Portfolio galleries", "Customer reviews", "Location-based matching"]
    },
    {
      icon: Camera,
      title: "Virtual Fitting & Measurements",
      description: "Get accurate measurements from home using our AI-powered measurement system. No more trips to the tailor for basic fittings.",
      features: ["AI measurement technology", "Step-by-step guidance", "Measurement storage", "Progress tracking"]
    },
    {
      icon: MessageSquare,
      title: "Direct Communication",
      description: "Chat directly with tailors, discuss designs, share inspiration images, and get real-time updates on your orders.",
      features: ["In-app messaging", "File sharing", "Voice notes", "Order updates"]
    },
    {
      icon: CreditCard,
      title: "Secure Payments",
      description: "Multiple payment options with secure escrow system. Pay only when you're satisfied with the final product.",
      features: ["Multiple payment methods", "Secure escrow", "Installment options", "Refund protection"]
    },
    {
      icon: Truck,
      title: "Order Tracking",
      description: "Track your order from design approval to final delivery. Get notifications at every stage of the process.",
      features: ["Real-time tracking", "Stage notifications", "Delivery updates", "Quality assurance"]
    },
    {
      icon: Star,
      title: "Quality Assurance",
      description: "Built-in quality checks and customer satisfaction guarantees. Your satisfaction is our priority.",
      features: ["Quality standards", "Satisfaction guarantee", "Dispute resolution", "Customer support"]
    }
  ];

  const tailorServices = [
    {
      icon: Store,
      title: "Digital Storefront",
      description: "Create a professional online presence with customizable profiles, portfolio galleries, and service listings.",
      features: ["Custom profiles", "Portfolio management", "Service packages", "Branding tools"]
    },
    {
      icon: Users,
      title: "Customer Discovery",
      description: "Reach customers beyond your local area. Our platform connects you with people actively seeking your skills.",
      features: ["Customer matching", "Lead generation", "Market insights", "Geographic expansion"]
    },
    {
      icon: BarChart3,
      title: "Business Analytics",
      description: "Track your performance, understand customer preferences, and optimize your business with detailed analytics.",
      features: ["Order analytics", "Customer insights", "Revenue tracking", "Performance metrics"]
    },
    {
      icon: MessageSquare,
      title: "Customer Management",
      description: "Manage all customer interactions, orders, and communications from one centralized dashboard.",
      features: ["Order management", "Customer database", "Communication tools", "History tracking"]
    },
    {
      icon: CreditCard,
      title: "Payment Processing",
      description: "Accept payments securely and get paid faster with our integrated payment system.",
      features: ["Multiple payment methods", "Fast settlements", "Financial reports", "Tax documentation"]
    },
    {
      icon: Zap,
      title: "Business Tools",
      description: "Access tools for measurements, order tracking, and customer relationship management.",
      features: ["Measurement tools", "Order templates", "Customer notes", "Workflow automation"]
    }
  ];

  const fashionHouseServices = [
    {
      icon: Store,
      title: "Enterprise Platform",
      description: "Scalable solutions for fashion houses with multiple designers, locations, and complex workflows.",
      features: ["Multi-location support", "Team management", "Advanced workflows", "Enterprise security"]
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Comprehensive business intelligence with detailed reporting, forecasting, and performance insights.",
      features: ["Business intelligence", "Sales forecasting", "Performance metrics", "Custom reports"]
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Enable seamless collaboration between designers, tailors, and customer service teams.",
      features: ["Role-based access", "Team communication", "Project management", "Workflow automation"]
    },
    {
      icon: Globe,
      title: "Multi-Market Support",
      description: "Expand your business across different regions with localized features and multi-language support.",
      features: ["Multi-language", "Local currencies", "Regional compliance", "Market insights"]
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-level security with advanced authentication, data encryption, and compliance features.",
      features: ["Advanced security", "Data encryption", "Compliance tools", "Audit trails"]
    },
    {
      icon: Zap,
      title: "API Integration",
      description: "Integrate with your existing systems and third-party tools for seamless operations.",
      features: ["REST APIs", "Webhook support", "Third-party integrations", "Custom development"]
    }
  ];

  const getServices = () => {
    switch (activeTab) {
      case 'customers':
        return customerServices;
      case 'tailors':
        return tailorServices;
      case 'fashion-houses':
        return fashionHouseServices;
      default:
        return customerServices;
    }
  };

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case 'customers':
        return Users;
      case 'tailors':
        return Scissors;
      case 'fashion-houses':
        return Store;
      default:
        return Users;
    }
  };

  const getTabColor = (tab: string) => {
    switch (tab) {
      case 'customers':
        return 'from-brand-blue to-brand-blue';
      case 'tailors':
        return 'from-brand-yellow to-brand-yellow';
      case 'fashion-houses':
        return 'from-green-500 to-green-500';
      default:
        return 'from-brand-blue to-brand-blue';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-white to-brand-blue/10">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-brand-blue rounded-full shadow-lg">
                <Zap className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Our <span className="text-brand-blue">Services</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions designed to transform the tailoring experience for everyone involved
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Categories Tabs */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { id: 'customers', label: 'For Customers', icon: Users },
              { id: 'tailors', label: 'For Tailors', icon: Scissors },
              { id: 'fashion-houses', label: 'For Fashion Houses', icon: Store }
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r ' + getTabColor(tab.id) + ' text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {activeTab === 'customers' && 'Customer Services'}
              {activeTab === 'tailors' && 'Tailor Services'}
              {activeTab === 'fashion-houses' && 'Fashion House Services'}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {activeTab === 'customers' && 'Everything you need to find the perfect tailor and get clothes that fit perfectly'}
              {activeTab === 'tailors' && 'Tools and features to grow your business and reach more customers'}
              {activeTab === 'fashion-houses' && 'Enterprise solutions for scaling your fashion business'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {getServices().map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                                     <div className={`flex items-center justify-center w-16 h-16 rounded-2xl mb-6 mx-auto ${
                     activeTab === 'customers' ? 'bg-brand-blue' :
                     activeTab === 'tailors' ? 'bg-brand-yellow' :
                     'bg-green-500'
                   }`}>
                     <Icon className="w-8 h-8 text-white" />
                   </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 text-center leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800 mb-3">Key Features:</h4>
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How Our <span className="text-brand-blue">Services</span> Work
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Simple, intuitive processes that make tailoring accessible and efficient for everyone
            </p>
          </motion.div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-brand-blue via-brand-yellow to-green-500 transform -translate-y-1/2 z-0"></div>
            
            <div className="grid md:grid-cols-3 gap-8 relative z-10">
              {[
                {
                  step: "1",
                  title: "Choose Your Service",
                  description: "Select from our range of services tailored to your specific needs and requirements",
                  icon: Search,
                  color: "from-brand-blue to-blue-600"
                },
                {
                  step: "2",
                  title: "Connect & Collaborate",
                  description: "Work directly with skilled professionals through our integrated communication tools",
                  icon: MessageSquare,
                  color: "from-brand-yellow to-yellow-600"
                },
                {
                  step: "3",
                  title: "Get Results",
                  description: "Receive high-quality, perfectly fitted clothing delivered to your satisfaction",
                  icon: CheckCircle,
                  color: "from-green-500 to-green-600"
                }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.step}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    className="text-center group"
                  >
                    {/* Step Number Circle */}
                    <div className="relative mb-8">
                      <div className={`w-24 h-24 bg-gradient-to-r ${item.color} rounded-full flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
                        <span className="text-3xl font-bold text-white">{item.step}</span>
                      </div>
                      
                      {/* Glow Effect */}
                      <div className={`absolute inset-0 w-24 h-24 bg-gradient-to-r ${item.color} rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300`}></div>
                    </div>
                    
                    {/* Icon Card */}
                    <div className="bg-white rounded-2xl p-6 shadow-lg mb-6 group-hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2 border border-gray-100">
                      <div className={`w-16 h-16 bg-gradient-to-r ${item.color} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
                    </div>
                    
                    {/* Progress Indicator */}
                    {index < 2 && (
                      <div className="hidden lg:flex items-center justify-center">
                        <div className="w-8 h-8 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center">
                          <div className="w-3 h-3 bg-brand-blue rounded-full"></div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-blue/5 to-brand-yellow/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Ready to <span className="text-brand-blue">Get Started</span>?
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Join thousands of customers and tailors who are already experiencing the future of custom tailoring
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 transition-colors font-medium flex items-center justify-center space-x-2">
                <span>Explore Services</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="px-8 py-4 border border-brand-blue text-brand-blue rounded-lg hover:bg-brand-blue/10 transition-colors font-medium">
                Contact Support
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
